import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiGet, apiPut } from "../lib/api";
import { relativeTime } from "../lib/format";
import { Alert, Button, Spinner, EmptyState } from "./ui";

/* Admin review queue. Lives on /gear/admin above the member list, because an
   open report is time-sensitive in a way that member admin isn't. */
export function ReportQueue() {
  const [reports, setReports] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [openCount, setOpenCount] = useState(0);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState("");

  async function load(all = showAll) {
    setError("");
    try {
      const data = await apiGet(`/admin/reports?status=${all ? "all" : "open"}`);
      setReports(data.reports || []);
      setOpenCount(data.openCount || 0);
    } catch (e) {
      setError(e.message || "Couldn't load reports.");
      setReports([]);
    }
  }

  useEffect(() => {
    load(showAll);
    // eslint-disable-line
  }, [showAll]);

  async function setStatus(r, status) {
    setBusyId(r.id);
    setError("");
    try {
      await apiPut(`/admin/reports/${r.id}`, { status });
      await load(showAll);
    } catch (e) {
      setError(e.message || "Couldn't update that report.");
    } finally {
      setBusyId("");
    }
  }

  if (reports === null) {
    return (
      <div className="mt-4 flex justify-center py-8">
        <Spinner />
      </div>
    );
  }

  return (
    <section className="mt-6">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-lg font-bold text-slate-800">
          Reported listings
          {openCount > 0 && (
            <span className="ml-2 rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">
              {openCount} open
            </span>
          )}
        </h2>
        <button
          onClick={() => setShowAll((v) => !v)}
          className="text-sm font-medium text-brand-600 hover:text-brand-700"
        >
          {showAll ? "Show open only" : "Show all"}
        </button>
      </div>

      {error && (
        <div className="mt-3">
          <Alert>{error}</Alert>
        </div>
      )}

      {reports.length === 0 ? (
        <div className="mt-2 rounded-xl border border-slate-200 bg-white">
          <EmptyState title={showAll ? "No reports yet" : "Nothing to review"}>
            {showAll
              ? "Members can report a listing from its page."
              : "No open reports right now."}
          </EmptyState>
        </div>
      ) : (
        <ul className="mt-3 divide-y divide-slate-200 rounded-xl border border-slate-200 bg-white">
          {reports.map((r) => {
            const busy = busyId === r.id;
            return (
              <li key={r.id} className="px-4 py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
                        {r.reasonLabel}
                      </span>
                      {r.status !== "open" && (
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-500">
                          {r.status === "actioned" ? "Actioned" : "Dismissed"}
                        </span>
                      )}
                      <span className="text-xs text-slate-400">{relativeTime(r.createdAt)}</span>
                    </div>
                    <div className="mt-1 font-medium text-slate-800">
                      {/* The listing may already be gone — the snapshot title is
                          why this row still makes sense either way. */}
                      <Link to={`/gear/listing/${r.listingId}`} className="hover:underline">
                        {r.listingTitle || "(untitled listing)"}
                      </Link>
                      {r.sellerName && (
                        <span className="font-normal text-slate-500"> — {r.sellerName}</span>
                      )}
                    </div>
                    {r.note && <p className="mt-1 text-sm text-slate-600">“{r.note}”</p>}
                    <div className="mt-1 text-xs text-slate-400">
                      Reported by {r.reporterName}
                      {r.resolvedBy && ` · resolved by ${r.resolvedBy}`}
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-col gap-2">
                    {r.status === "open" ? (
                      <>
                        <Button variant="secondary" disabled={busy} onClick={() => setStatus(r, "dismissed")}>
                          {busy ? <Spinner /> : "Dismiss"}
                        </Button>
                        <Button variant="secondary" disabled={busy} onClick={() => setStatus(r, "actioned")}>
                          Mark handled
                        </Button>
                      </>
                    ) : (
                      <Button variant="secondary" disabled={busy} onClick={() => setStatus(r, "open")}>
                        {busy ? <Spinner /> : "Reopen"}
                      </Button>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <p className="mt-2 text-xs text-slate-400">
        To take a listing down, open it and use Remove — that also clears its reports.
      </p>
    </section>
  );
}
